import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import html2canvas from 'html2canvas';
import { TerraDraw, TerraDrawRectangleMode } from 'terra-draw';
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter';
import { ExportMapModalComponent } from '../../shared/export-map-modal/export-map-modal.component';
import {
  ANODE,
  BOND,
  CASING,
  COMPLIANT_AH,
  CTS,
  DELINQUENT_AH,
  EMPTY_STRING,
  ER_PROBE,
  EXCEPTION_AH,
  ISOLATIONJOINT,
  RECTIFIER,
  TEST_POINT,
} from '../../shared/constants';

// google is loaded globally by index.html's Maps JS script tag.
declare const google: any;

@Injectable({
  providedIn: 'root',
})
export class MapService {
  private mapElement!: HTMLElement;
  private mapInstance: any = null;
  private center: { lat: number; lng: number } | undefined;
  private terraDraw: any = null;
  constructor(public dialog: MatDialog) {}

  /**
   * SYRC-6889: Shared rectangle-draw-to-select wrapper. Replaces the deprecated
   * google.maps.drawing.DrawingManager with Terra Draw. Call once per map; it
   * adds a rectangle-draw toggle button to the map (TOP_CENTER, matching the old
   * DrawingManager control position). When the user clicks it, rectangle mode is
   * armed; the user drags one rectangle, then `onBounds` is invoked with a
   * google.maps.LatLngBounds, the drawn shape is cleared (disappears on release),
   * and the map returns to normal (non-draw) mode — one rectangle per click,
   * exactly like the old DrawingManager's drawingMode:null-after-complete behavior.
   * Callers keep their existing bounds.contains(markerPosition) logic unchanged.
   */
  startRectangleSelect(
    map: any,
    mapElementId: string,
    onBounds: (bounds: any) => void,
  ): void {
    // MapService is a root singleton shared by both map components. Tear down
    // any previous Terra Draw instance before creating a new one, otherwise the
    // old instance's adapter keeps its listeners/OverlayView attached to the
    // previous map container as zombies after navigation.
    this.stopRectangleSelect();

    // The Google Maps adapter needs the map container element to have an id.
    const mapDiv: HTMLElement | null = map?.getDiv ? map.getDiv() : null;
    if (mapDiv && !mapDiv.id) {
      mapDiv.id = mapElementId;
    }

    const styles = {
      fillColor: '#FF0000',
      fillOpacity: 0.2,
      outlineColor: '#FF0000',
      outlineWidth: 2,
    } as any;

    const adapter = new TerraDrawGoogleMapsAdapter({
      lib: google.maps,
      map,
      coordinatePrecision: 9,
    });

    // Note: terra-draw-google-maps-adapter@1.6.2 locates the pointer-event
    // element via the brittle selector div[style*="z-index: 3;"] inside
    // map.getDiv(). On this app's map that node isn't present at register()
    // time, so querySelector returns null and the adapter crashes with
    // "Cannot read properties of null (reading 'addEventListener')". Override
    // getMapEventElement to fall back to the always-present map container.
    // Upgrade path: drop this override once the adapter stops relying on the
    // z-index selector (see JamesLMilner/terra-draw issue #731).
    const adapterAny = adapter as any;
    const originalGetMapEventElement =
      adapterAny.getMapEventElement?.bind(adapterAny);
    // Fail loudly if an adapter upgrade removes/renames this internal method, so
    // the override silently becoming a no-op (and the register() crash returning
    // at runtime) is caught here instead of in production.
    if (typeof originalGetMapEventElement !== 'function') {
      console.error(
        'MapService: terra-draw-google-maps-adapter getMapEventElement override ' +
          'is no longer applicable (method missing). Re-check the adapter version ' +
          '(see JamesLMilner/terra-draw issue #731).',
      );
    }
    adapterAny.getMapEventElement = (eventType?: string): HTMLElement => {
      const el = originalGetMapEventElement
        ? originalGetMapEventElement(eventType)
        : null;
      return el ?? (map.getDiv() as HTMLElement);
    };

    const draw = new TerraDraw({
      adapter,
      // Start inert ('static'): the map pans/clicks normally until the user
      // explicitly arms rectangle mode via the control button below.
      modes: [
        new TerraDrawRectangleMode({ styles, drawInteraction: 'click-drag' }),
      ],
    });
    this.terraDraw = draw;

    draw.start();

    // Add the rectangle-draw toggle button at TOP_CENTER (old DrawingManager
    // position). Clicking it arms rectangle mode for a single draw and shows a
    // pressed/active state + crosshair cursor; both reset once drawing finishes.
    let armed = false;
    const setArmed = (active: boolean): void => {
      armed = active;
      control.setActive(active);
      // While armed: crosshair cursor and disable map panning so a click-drag
      // draws the rectangle instead of dragging the map (the two compete and
      // cause the draw to get stuck). Restore both when idle. Mirrors the old
      // DrawingManager, which also suppressed panning during rectangle draw.
      map.setOptions({
        draggableCursor: active ? 'crosshair' : null,
        draggable: !active,
        gestureHandling: active ? 'none' : 'auto',
      });
    };
    const control = this.buildRectangleControl(() => {
      if (armed) return; // ignore repeat clicks while already drawing
      setArmed(true);
      // Defer arming to the next tick so the button's own click/pointer
      // sequence fully settles before Terra Draw starts listening for draw
      // input. Terra Draw's Google adapter listens on the map container, which
      // also contains this control button; arming synchronously lets the button
      // press itself seed a rectangle (box appears without the user dragging).
      setTimeout(() => {
        if (armed) draw.setMode('rectangle');
      }, 0);
    });

    const ready = () => {
      // Keep the map inert until the button is clicked.
      draw.setMode('static');
      map.controls[google.maps.ControlPosition.TOP_CENTER].push(
        control.element,
      );
    };
    // The Google adapter builds its OverlayView asynchronously; wait for ready.
    draw.on('ready', ready);

    draw.on('finish', (id: any) => {
      // Only react while an armed draw session is active. We guard on `armed`
      // (not context.action) because the finish action differs by interaction:
      // 'click-drag' completion reports a drag action, 'click-move' reports
      // 'draw' — guarding on the action would skip clearing for click-drag and
      // leave the rectangle stuck on the map.
      if (!armed) return;

      const feature = draw
        .getSnapshot()
        .find((f: any) => f.id === id && f.geometry?.type === 'Polygon');
      // Clear the drawn shape first so the rectangle disappears on release,
      // then return to the inert mode (one rectangle per button click) and
      // reset the button/cursor to their default styles.
      // Clearing only inside `finish` avoids corrupting the store mid-draw.
      draw.clear();
      draw.setMode('static');
      setArmed(false);

      if (!feature) return;
      const ring = (feature.geometry as any).coordinates[0] as number[][];
      const bounds = this.boundsFromRectangleRing(ring);
      if (bounds) onBounds(bounds);
    });
  }

  /**
   * SYRC-6889: Tears down the active Terra Draw instance and its adapter
   * listeners/OverlayView. Safe to call when none exists. Call from each map
   * component's ngOnDestroy so navigating away doesn't leave zombie listeners
   * on the old map container (MapService is a root singleton).
   */
  stopRectangleSelect(): void {
    if (this.terraDraw) {
      try {
        this.terraDraw.stop();
      } catch {
        // stop() can throw if the instance was never fully started; ignore.
      }
      this.terraDraw = null;
    }
  }

  /**
   * SYRC-6889: Builds the rectangle-draw control button, styled to sit in the
   * Google Maps control strip. `onArm` fires when the user clicks it. Returns
   * the element plus a `setActive` toggle so the caller can reflect the
   * drawing/pressed state (and reset it when drawing finishes).
   */
  private buildRectangleControl(onArm: () => void): {
    element: HTMLElement;
    setActive: (active: boolean) => void;
  } {
    const button = document.createElement('button');
    button.type = 'button';
    button.title = 'Draw a rectangle to select assets';
    button.setAttribute('aria-label', 'Draw a rectangle to select assets');
    button.setAttribute('aria-pressed', 'false');
    button.textContent = 'Draw rectangle';

    const idleStyle = {
      background: '#fff',
      boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
      color: 'rgb(86,86,86)',
    };
    const activeStyle = {
      // Pressed/active look: tinted + inset shadow, matching the red draw accent.
      background: '#FFEBEB',
      boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.3)',
      color: '#C62828',
    };

    Object.assign(button.style, {
      margin: '10px',
      padding: '6px 12px',
      border: 'none',
      borderRadius: '2px',
      cursor: 'pointer',
      fontFamily: 'Roboto, Arial, sans-serif',
      fontSize: '14px',
      ...idleStyle,
    });

    // Stop the button's whole pointer sequence from reaching the map/Terra
    // Draw listener underneath — otherwise the press that arms the mode is also
    // read as the rectangle's first draw point (box appears without dragging).
    ['pointerdown', 'pointerup', 'mousedown', 'mouseup'].forEach((type) => {
      button.addEventListener(type, (e) => {
        e.stopPropagation();
      });
    });
    button.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      onArm();
    });

    const setActive = (active: boolean): void => {
      Object.assign(button.style, active ? activeStyle : idleStyle);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    };

    return { element: button, setActive };
  }

  /**
   * SYRC-6889: Pure helper. Given a GeoJSON polygon outer ring ([[lng,lat],...])
   * computes the SW/NE corners and returns a google.maps.LatLngBounds so callers
   * can run bounds.contains(markerPosition) exactly as before. Validates the ring
   * is a well-formed rectangle (>= 4 coordinate pairs) — a trust boundary on the
   * GeoJSON shape coming out of Terra Draw.
   */
  boundsFromRectangleRing(ring: number[][]): any {
    if (!Array.isArray(ring) || ring.length < 4) {
      return null;
    }
    let minLat = Infinity;
    let minLng = Infinity;
    let maxLat = -Infinity;
    let maxLng = -Infinity;
    for (const point of ring) {
      const [lng, lat] = point;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
    }
    return new google.maps.LatLngBounds(
      { lat: minLat, lng: minLng },
      { lat: maxLat, lng: maxLng },
    );
  }

  initialize(mapElement: HTMLElement, mapInstance: any) {
    this.mapElement = mapElement;
    this.mapInstance = mapInstance;
  }

  assetData: any[] = [];
  getData() {
    this.setMapCenter();
    return this.assetData;
  }

  openExportMapModal(): void {
    const dialogRef = this.dialog.open(ExportMapModalComponent);
    dialogRef
      .afterClosed()
      .subscribe(
        (
          result:
            | { fileType: string; includeLegendsMarkers: boolean }
            | undefined,
        ) => {
          if (result) {
            this.exportMapAsImage(
              result.fileType,
              result.includeLegendsMarkers,
            );
          } else {
            console.log(
              'No file type selected or dialog closed without selection.',
            );
          }
        },
      );
  }

  pickMarkerColor(assetType: string): string {
    switch (assetType) {
      case COMPLIANT_AH:
        return '#22C55E';
      case EXCEPTION_AH:
        return '#E5AB09';
      case DELINQUENT_AH:
        return '#E77A3C';
      default:
        return EMPTY_STRING;
    }
  }

  pickMarkerInnerText(assetType: string): string {
    let markertext = 'M';
    if (assetType == RECTIFIER) {
      markertext = 'R';
    } else if (assetType == TEST_POINT) {
      markertext = 'T';
    } else if (assetType == BOND) {
      markertext = 'B';
    } else if (assetType == ANODE) {
      markertext = 'A';
    } else if (assetType == CASING) {
      markertext = 'CS';
    } else if (assetType == CTS) {
      markertext = 'CP';
    } else if (assetType == ER_PROBE) {
      markertext = 'ER';
    } else if (assetType == ISOLATIONJOINT) {
      markertext = 'IJ';
    } else {
      const firstLetter = assetType.charAt(0).toUpperCase();
      markertext = firstLetter;
    }
    return markertext;
  }

  getMarkerIcon(
    text: string,
    markerColor: string,
    shoText: boolean,
    type: string,
  ): string {
    const numberElements = shoText
      ? `
        <circle cx="38" cy="11" r="10" fill="#3498DB" stroke="white" stroke-width="2"/>
        <text x="38" y="12" font-family="Arial" font-size="14" fill="#FFF" text-anchor="middle" alignment-baseline="middle">${text}</text>
      `
      : '';

    const svgIcon = `
      <svg width="98" height="110" viewBox="0 0 49 55" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clip-path="url(#clip0)">
          <g filter="url(#filter0_d)">
            <!-- Marker path with border -->
            <path d="M24.15 10.0244C16.3616 10.0244 10.0625 16.3235 10.0625 24.1119C10.0625 34.6775 24.15 50.2744 24.15 50.2744C24.15 50.2744 38.2375 34.6775 38.2375 24.1119C38.2375 16.3235 31.9384 10.0244 24.15 10.0244Z" fill="${markerColor}" stroke="white" stroke-width="2"/>
            <text x="24.15" y="27" font-family="Arial" font-size="16" fill="white" text-anchor="middle" alignment-baseline="middle">${type}</text>
          </g>
        </g>
        ${numberElements}
        <defs>
          <filter id="filter0_d" x="1.0625" y="5.02441" width="46.1748" height="59.2334" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
            <feFlood flood-opacity="0" result="BackgroundImageFix"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dy="4"/>
            <feGaussianBlur stdDeviation="3.5"/>
            <feComposite in2="hardAlpha" operator="out"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow"/>
            <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape"/>
          </filter>
          <clipPath id="clip0">
            <rect y="6" width="60.3" height="60.3" rx="2.24651" fill="white"/>
          </clipPath>
        </defs>
      </svg>
    `;

    // Return the SVG as a data URL
    return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgIcon);
  }
  exportMapAsImage(fileType: string, includeLegendsMarkers: boolean): void {
    if (!fileType || !this.mapElement || !this.mapInstance) {
      console.error('Required data is missing');
      return;
    }
    const mapContainer = this.mapElement.closest('.map-container');

    if (mapContainer) {
      // Hide zoom and map type controls
      this.mapInstance.setOptions({
        zoomControl: false,
        mapTypeControl: false,
        fullscreenControl: false,
      });
      const legendContainer = mapContainer.querySelector(
        '.legend-container',
      ) as HTMLElement;
      let originalDisplayStyle = '';

      if (legendContainer) {
        originalDisplayStyle = window.getComputedStyle(legendContainer).display;
        if (!includeLegendsMarkers) {
          legendContainer.style.display = 'none';
        }
      }

      // Function to handle the image capture
      const captureImage = () => {
        const captureElement = (
          includeLegendsMarkers ? mapContainer : this.mapElement
        ) as HTMLElement;
        html2canvas(captureElement, {
          useCORS: true,
          scrollX: 0,
          scrollY: 0,
          scale: 2,
        })
          .then((canvas) => {
            const image = canvas.toDataURL(`image/${fileType}`);
            const link = document.createElement('a');
            link.href = image;
            link.download = `map.${fileType}`;
            link.click();
          })
          .catch((error) => {
            console.error('Error capturing map image:', error);
          })
          .finally(() => {
            // Restore original map options
            this.mapInstance.setOptions({
              zoomControl: true,
              mapTypeControl: true,
              fullscreenControl: true,
            });

            if (legendContainer) {
              legendContainer.style.display = originalDisplayStyle;
            }
          });
      };
      setTimeout(captureImage, 100);
    } else {
      console.error('Map element or container is not found.');
    }
  }

  private setMapCenter(): void {
    if (this.assetData.length > 0) {
      const latitudes = this.assetData.map((data) => parseFloat(data.latitude));
      const longitudes = this.assetData.map((data) =>
        parseFloat(data.longitude),
      );

      const avgLatitude = latitudes[0];
      const avgLongitude = longitudes[0];

      this.center = { lat: avgLatitude, lng: avgLongitude };
    } else if (this.assetData.length == 0) {
      this.center = { lat: 44.21839866380285, lng: -104.11857786015787 };
    } else {
      console.error('No asset data available to calculate center.');
    }
  }

  getMapCenter(): { lat: number; lng: number } | undefined {
    this.setMapCenter();
    return this.center;
  }
}
